import React, { useState, useEffect } from 'react';
import { ImageList, ImageListItem, Box, Modal, IconButton } from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { Close as CloseIcon, ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';

interface MediaFile {
  url: string;
  mimeType: string;
  name: string;
}

interface MediaGalleryProps {
  files: MediaFile[];
  watermarkUrl?: string;
  watermarkSize?: number;
  watermarkOpacity?: number;
}

const ImageGallery: React.FC<MediaGalleryProps> = ({
  files,
  watermarkUrl = '/logo-watermark.png',
  watermarkSize = 150,
  watermarkOpacity = 0.12,
}) => {
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const images = files.filter((f) => f.mimeType.startsWith('image/'));
  const videos = files.filter((f) => f.mimeType.startsWith('video/'));

  const handleImageClick = (image: MediaFile) => {
    const index = images.findIndex((img) => img.url === image.url);
    setCurrentIndex(index);
    setSelectedFile(image);
  };

  const handleCloseModal = () => setSelectedFile(null);
  const handlePrevious = () => {
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedFile(images[newIndex]);
  };
  const handleNext = () => {
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedFile(images[newIndex]);
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedFile) return;
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') handleCloseModal();
    };
    const handleContextMenu = (e: MouseEvent) => {
      if (e.target instanceof HTMLImageElement) e.preventDefault();
    };
    window.addEventListener('keydown', handleKeyPress);
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [selectedFile, currentIndex]);

  const WatermarkOverlay = ({
    size = watermarkSize,
    opacity = watermarkOpacity,
  }: {
    size?: number;
    opacity?: number;
  }) => (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backgroundImage: `url("${watermarkUrl}")`,
        backgroundRepeat: 'repeat',
        backgroundSize: `${size}px ${size}px`,
        opacity,
        mixBlendMode: 'multiply',
      }}
    />
  );

  return (
    <>
      <GlobalStyles
        styles={{
          '.ih-no-save': {
            userSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          },
          '.ih-no-save::-webkit-scrollbar': { display: 'none' },
          '.ih-no-save img': { WebkitUserDrag: 'none', userSelect: 'none' },
        }}
      />

      <Box
        className="ih-no-save"
        sx={{ width: '100%', overflow: 'hidden' }}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      >
        {images.length > 0 && (
          <ImageList cols={4} gap={16} sx={{ mb: videos.length > 0 ? 2 : 0, overflow: 'hidden' }}>
            {images.map((image, index) => (
              <ImageListItem
                key={image.url}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  borderRadius: 2,
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  height: 200,
                  '&:hover': { transform: 'scale(1.05)', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
                }}
                onClick={() => handleImageClick(image)}
                onContextMenu={(e) => e.preventDefault()}
              >
                <img
                  src={image.url}
                  alt={`Imagen ${index + 1}`}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  draggable={false}
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = 'none';
                  }}
                />
                {/* Marca de agua repetida */}
                <WatermarkOverlay />
              </ImageListItem>
            ))}
          </ImageList>
        )}

        {videos.length > 0 && (
          <ImageList
            cols={2}
            gap={16}
            sx={{
              overflow: 'hidden',
              '& .MuiImageListItem-root': {
                height: 250,
                overflow: 'hidden',
                borderRadius: 2,
                position: 'relative',
              },
            }}
          >
            {videos.map((video, index) => (
              <ImageListItem key={`video-${index}`}>
                <video
                  src={video.url}
                  controls
                  preload="metadata"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}

        <Modal
          open={!!selectedFile}
          onClose={handleCloseModal}
          closeAfterTransition
          sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.9)' } }}
        >
          <Box
            className="ih-no-save"
            sx={{
              position: 'fixed',
              inset: 0,
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            {images.length > 1 && (
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: 20,
                  zIndex: 1,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ArrowBackIos />
              </IconButton>
            )}
            {images.length > 1 && (
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 20,
                  zIndex: 1,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ArrowForwardIos />
              </IconButton>
            )}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                zIndex: 1,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>

            {selectedFile && (
              <Box sx={{ position: 'relative' }}>
                <img
                  src={selectedFile.url}
                  alt="Imagen completa"
                  style={{
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                  draggable={false}
                />
                {/* Marca de agua en el modal (un poco más grande) */}
                <WatermarkOverlay size={200} opacity={watermarkOpacity} />
              </Box>
            )}
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default ImageGallery;
